import { motion } from "framer-motion"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { PageContainer, PageSection } from "@/components/layout/PageShell"
import { cn } from "@/lib/utils"

type TrustStripProps = {
  title: string
  description: string
  variant?: "card" | "split"
  sectionClassName?: string
  logoSrc?: string
  logoAlt?: string
  partnerName?: string
  partnerSubtitle?: string
  partnerDescription?: string
  ctaLabel?: string
  ctaHref?: string
  ctaExternal?: boolean
  imageSrc?: string
  imageAlt?: string
}

export function TrustStrip({
  title,
  description,
  variant = "card",
  sectionClassName,
  logoSrc = "/images/logo_tca.png",
  logoAlt = "Thermax Channel Associate",
  partnerName = "Thermax Limited",
  partnerSubtitle = "Channel Associate",
  partnerDescription = description,
  ctaLabel,
  ctaHref,
  ctaExternal,
  imageSrc,
  imageAlt = "Team at work",
}: TrustStripProps) {
  const isExternalCta = Boolean(ctaHref) && (ctaExternal ?? /^https?:\/\//.test(ctaHref ?? ""))

  if (variant === "split") {
    return (
      <PageSection className={cn("bg-slate-100", sectionClassName)}>
        <PageContainer>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{title}</h2>
              <p className="text-slate-600 leading-relaxed mb-6">{description}</p>
              <div className="flex items-center gap-6">
                <img src={logoSrc} alt={logoAlt} className="h-20 w-auto" />
                <div>
                  <div className="text-slate-900 font-semibold">{partnerName}</div>
                  <div className="text-slate-600 text-sm">{partnerSubtitle}</div>
                </div>
              </div>
            </motion.div>

            {imageSrc && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="aspect-video rounded-2xl overflow-hidden"
              >
                <img src={imageSrc} alt={imageAlt} className="w-full h-full object-cover" />
              </motion.div>
            )}
          </div>
        </PageContainer>
      </PageSection>
    )
  }

  return (
    <PageSection className={cn("bg-slate-50", sectionClassName)}>
      <PageContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{title}</h2>
          <p className="text-slate-500 max-w-2xl mx-auto">{description}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="shrink-0">
              <img src={logoSrc} alt={logoAlt} className="w-48 h-auto" />
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{partnerName}</h3>
              <p className="text-slate-500 mb-6">{partnerDescription}</p>

              {ctaLabel && ctaHref && (
                <Button variant="outline" asChild>
                  <a
                    href={ctaHref}
                    target={isExternalCta ? "_blank" : undefined}
                    rel={isExternalCta ? "noopener noreferrer" : undefined}
                  >
                    {ctaLabel}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </PageContainer>
    </PageSection>
  )
}
